/**
 * GlobalConsentInterceptor — App-shell-level re-consent interceptor.
 *
 * Sprint: CONSENT-RECONSENT-INTERCEPTOR-AUDIT-AND-IMPLEMENTATION-01
 * Authority: /app/memory/ARCHITECTURE_GUARDRAILS.md Parts G + H
 *
 * Mounts ONCE inside <AuthProvider> at the app shell. On auth-state
 * change, fetches /api/user/me/consents/pending and:
 *
 *   * For each Class-A row that the canonical binding declares
 *     ACCOUNT-WIDE (`app_shell_blocking`): renders a non-dismissable
 *     <ConsentGate> modal sequentially. Flow-bound requirements are
 *     NEVER queued here — they are enforced at their own business
 *     process and by the server-side transaction guards.
 *
 *   * For each Class-B row (materiality="B"): fires a single sonner
 *     toast per browser session per key (de-duped by sessionStorage).
 *
 * Behavioural invariants:
 *   - Unauthenticated → renders nothing
 *   - response.interceptor_enabled === false → renders nothing
 *     (lets us ship the route + UI decoupled from launch)
 *   - Class-A modal stack is non-dismissable; the only escape valve
 *     is to log out (header logout button is unaffected)
 *
 * No new auth code. No schema changes. No commercial-flow refusal.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ConsentGate } from './ConsentGate';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const SOFT_NOTICE_DISMISS_KEY = 'treatx_consent_soft_notice_dismissed';

function readDismissedSet() {
  try {
    const raw = sessionStorage.getItem(SOFT_NOTICE_DISMISS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function persistDismissed(set) {
  try {
    sessionStorage.setItem(SOFT_NOTICE_DISMISS_KEY, JSON.stringify([...set]));
  } catch {
    /* sessionStorage may be unavailable (private mode) — non-blocking */
  }
}

export function GlobalConsentInterceptor() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const lastFetchedKey = useRef(null);

  // Fetch pending consents on auth-state change.
  const fetchPending = useCallback(async () => {
    if (!user?.id) {
      setPending([]);
      setEnabled(false);
      setActiveIdx(0);
      lastFetchedKey.current = null;
      return;
    }
    // CONSENT-INTERCEPTOR-REMEDIATION-01 — the cache key used to be the
    // bare user id, so a role/workspace switch within one session never
    // re-evaluated. Key on identity + role and reset on sign-out so no
    // consent state can leak across accounts.
    const identityKey = `${user.id}:${user.role || user.user_type || ''}`;
    if (lastFetchedKey.current === identityKey) return;
    lastFetchedKey.current = identityKey;

    try {
      const { data } = await api.get('/user/me/consents/pending');
      // A response that resolves after the identity changed (or after
      // sign-out) must never be applied to the new account.
      if (lastFetchedKey.current !== identityKey) return;
      const isEnabled = !!data?.interceptor_enabled;
      const list = Array.isArray(data?.pending_consents) ? data.pending_consents : [];
      setEnabled(isEnabled);
      setPending(list);
      setActiveIdx(0);

      if (!isEnabled) return;

      // Fire soft-notice toasts (Class-B) — once per session per key.
      const dismissed = readDismissedSet();
      list
        .filter((r) => r.materiality === 'B')
        .forEach((r) => {
          const dedupeKey = `${r.content_key}@${r.current_version}`;
          if (dismissed.has(dedupeKey)) return;
          toast.message(
            `Notice updated`,
            {
              description: `${r.content_key} was updated. View in Settings → Legal.`,
              duration: 10000,
              action: {
                label: 'Dismiss',
                onClick: () => {
                  const next = readDismissedSet();
                  next.add(dedupeKey);
                  persistDismissed(next);
                },
              },
            }
          );
        });
    } catch (err) {
      // Best-effort: never block the app shell on this read.
      console.warn('[GlobalConsentInterceptor] fetch failed:', err?.message);
      if (lastFetchedKey.current !== identityKey) return;
      setPending([]);
      setEnabled(false);
    }
  }, [user?.id, user?.role, user?.user_type]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // CONSENT-INTERCEPTOR-REMEDIATION-01 (Owner decision Q2) — the queue may
  // only contain requirements the CANONICAL binding declares account-wide.
  // A requirement carrying `blocking_flows` is enforced at that business
  // process (flow surface + server-side transaction guard), never as an
  // application-wide modal at login. `app_shell_blocking` is computed by
  // the canonical reader; a legacy response without the field falls back
  // to the same rule locally (Class-A AND no flow binding).
  const queue = enabled
    ? pending.filter((r) => {
        if (r.materiality !== 'A') return false;
        if (typeof r.app_shell_blocking === 'boolean') return r.app_shell_blocking;
        return !(Array.isArray(r.blocking_flows) && r.blocking_flows.length > 0);
      })
    : [];

  // Advance to the next Class-A modal after a successful accept. When the
  // queue is exhausted, re-read the canonical state for the SAME identity
  // instead of trusting the local index — otherwise a requirement that
  // became due mid-session is never re-evaluated (the cache key alone
  // would suppress the refetch).
  //
  // The refresh runs OUTSIDE the state updater: React may invoke an updater
  // more than once (Strict Mode double-invocation), so a fetch inside it
  // produced duplicate requests. The updater is now pure and the effect is
  // sequenced from the event handler, which runs exactly once per accept.
  const handleConsented = useCallback(() => {
    const next = activeIdx + 1;
    if (next < queue.length) {
      setActiveIdx(next);
      return;
    }
    setActiveIdx(0);
    lastFetchedKey.current = null;
    fetchPending();
  }, [activeIdx, queue.length, fetchPending]);

  if (!enabled) return null;
  if (queue.length === 0) return null;
  if (activeIdx >= queue.length) return null;

  const current = queue[activeIdx];

  return (
    <div data-testid="global-consent-interceptor">
      <ConsentGate
        contentKey={current.content_key}
        flow="reconsent_interceptor"
        country={null}
        onConsented={handleConsented}
      >
        {/* ConsentGate renders children when CURRENT — null is fine */}
        {null}
      </ConsentGate>
    </div>
  );
}

export default GlobalConsentInterceptor;
